// The Sanctum's voice: dry, occult-blog, anti-hype. Where a casino screams "JACKPOT!!",
// the Sanctum reframes the 55%-nothing as an offering to Eris and never dwells on a loss.
// Grounded in Server/LORE.md + VISUAL_IDENTITY.md. No emoji vomit; the apple earns its 🍎.

import type { Mode } from './types';

// The persistent line where a casino would put "WINNER". Radical transparency IS the brand.
export const HOUSE_LINE =
  'The house edge is real and published. The roll is not the house’s to fake — recompute it yourself.';

export const SEAL_LINE = 'The envelope is sealed. The wheel can’t be watching your face.';

export const RECONNECT_LINE =
  'Reconnecting… your spin is already sealed. Whatever it was, nothing is lost.';

export const VERIFY_PASS_LINE = 'Recomputed in your browser. The roll was exactly what the seal promised.';
export const VERIFY_FAIL_LINE = 'A spin did not match its seal. Screenshot this and tell the Wizard.';

export const GUILD_GATE_LINE =
  'This wheel turns only inside the Wizard’s Sanctum. The Arcade has nothing for you here.';

export const DAILY_CAP_LINE = 'That’s the day. Your free spin waits for Sunday.';
// The broke state is the most-seen terminal screen in the game — a door closing gracefully,
// pointing at the two things that are free. Never an error.
export const INSUFFICIENT_LINE =
  'The altar asks 50 and the purse is light. The Sunday ticket costs nothing — and tomorrow’s quests refill the hand.';

// First visit only (localStorage-flagged): one line of ritual, then the fast path forever.
export const RITUAL_LINE =
  'First time at the altar: the seal above is the wheel’s promise, fixed before you offer. Spin, then verify it yourself.';

// The pity register — a ledger line, deliberately NOT a filling bar (no goal-gradient bait).
export function pityLine(count: number, threshold: number): string {
  return `eris keeps a ledger too — ${count}/${threshold}`;
}

// The shared candle growing by the rake, shown on the offering card: a solo loss is also a
// group contribution, and the ledger says so.
export function candleLine(potBefore: number, rakeFs: number): string {
  return `the candle takes its five — ${potBefore} → ${potBefore + rakeFs} FS`;
}

// The result card, honest edition. The headline is the NET balance movement — never the
// gross prize. Only jackpot is ever net-positive on a paid spin; a `+15 FS` on a 50 FS
// stake is a loss disguised as a win (Dixon 2010) and is banned alongside the engineered
// near-miss. Green marks net-positive only; brick marks the burn; free spins (no stake)
// may celebrate what arrived.
export interface ResultCopy {
  big: string; // headline; '' means the card carries only the line (golden)
  sub: string;
  tone: 'gain' | 'loss' | 'neutral' | 'golden';
}

export function resultCopy(
  tier: string,
  netFs: number,
  prizeFs: number,
  jackpotWin: number,
  mode: Mode,
): ResultCopy {
  if (tier === 'golden_ticket') {
    return { big: '', sub: outcomeLine(tier, prizeFs, jackpotWin), tone: 'golden' };
  }
  if (mode === 'free') {
    // No stake, no FS legs — the free table pays in kind or not at all.
    if (tier === 'spin_ticket') {
      return { big: '+1 Spin Ticket', sub: 'The wheel re-arms itself.', tone: 'gain' };
    }
    if (tier === 'common_item') {
      return { big: '+1 Trinket', sub: outcomeLine(tier, prizeFs, jackpotWin), tone: 'gain' };
    }
    return { big: '—', sub: 'The void keeps it. The ticket cost you nothing.', tone: 'neutral' };
  }
  const tone: ResultCopy['tone'] = netFs > 0 ? 'gain' : netFs < 0 ? 'loss' : 'neutral';
  const big = netFs > 0 ? `+${netFs} FS` : netFs < 0 ? `−${Math.abs(netFs)} FS` : '±0 FS';
  switch (tier) {
    case 'fs_5_25':
      return { big, sub: `${prizeFs} came back. Eris kept the difference.`, tone };
    case 'spin_ticket':
      return { big, sub: 'A ticket falls out of the void — the wheel re-arms.', tone };
    case 'jackpot':
      return { big, sub: outcomeLine(tier, prizeFs, jackpotWin), tone };
    default:
      return { big, sub: outcomeLine(tier, prizeFs, jackpotWin), tone };
  }
}

// Outcome flavor, keyed by tier. {n} is filled with the FS amount where relevant.
export function outcomeLine(tier: string, prizeFs: number, jackpotWin: number): string {
  switch (tier) {
    case 'nothing':
      return 'The pool burns. Eris accepts your offering.';
    case 'fs_5_25':
      return `${prizeFs} FS finds its way back to your hand.`;
    case 'spin_ticket':
      return 'A ticket falls out of the void. One more, on the house.';
    case 'common_item':
      return 'A small trinket for the shelf. The Sanctum keeps its promises. Mostly.';
    case 'jackpot':
      return `🍎 The pot broke — ${jackpotWin} FS. The Void resets the candle.`;
    case 'golden_ticket':
      return 'The golden apple rolls to you. It is a key, not a prize — kallisti.';
    default:
      return 'The wheel settles.';
  }
}

// Short label for the wedge / result card headline (kept terse; flavor lives in outcomeLine).
export function tierLabel(tier: string): string {
  const labels: Record<string, string> = {
    nothing: 'Offering',
    fs_5_25: 'FlaxScript',
    spin_ticket: 'Spin Ticket',
    common_item: 'Trinket',
    jackpot: 'The Pot',
    golden_ticket: 'Golden Apple',
  };
  return labels[tier] ?? tier;
}

export function modeLabel(mode: Mode): string {
  return mode === 'free' ? 'the Sunday ticket' : 'a 50 FS offering';
}
